import { useState, useEffect, useRef } from "react";
import { Header, BottomNav } from "../components/Header";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Upload, Smartphone, Download, Link2, Loader2, CheckCircle2, AlertCircle, Image as ImageIcon, RotateCcw, Trash2, Check } from "lucide-react";

const AdminAppConfigPage = () => {
  const { user, token } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [manualUrl, setManualUrl] = useState("");
  const [version, setVersion] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [adminWhatsapp, setAdminWhatsapp] = useState("");
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);
  const [logoLibrary, setLogoLibrary] = useState([]);
  const [selectingLogoId, setSelectingLogoId] = useState(null);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB per chunk

  useEffect(() => {
    fetchConfig();
    fetchLogoLibrary();
  }, []);

  const fetchLogoLibrary = async () => {
    try {
      const response = await axios.get(`${API}/admin/app-config/logo-library`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setLogoLibrary(response.data || []);
    } catch (error) {
      console.error("Error fetching logo library:", error);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/app-config`);
      setConfig(response.data);
      setManualUrl(response.data.apk_url || "");
      setVersion(response.data.apk_version || "");
      setAdminWhatsapp(response.data.admin_whatsapp_number || "");
    } catch (error) {
      toast.error("Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWhatsappNumber = async () => {
    setSavingWhatsapp(true);
    try {
      await axios.put(`${API}/admin/app-config/whatsapp-number`, { admin_whatsapp_number: adminWhatsapp }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Número de WhatsApp guardado");
    } catch (error) {
      toast.error("Error al guardar el número");
    } finally {
      setSavingWhatsapp(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.apk')) {
      toast.error("Solo se permiten archivos .apk");
      return;
    }

    if (file.size > 200 * 1024 * 1024) {
      toast.error("El archivo es demasiado grande (máx 200MB)");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      // Step 1: init chunked upload session
      const initRes = await axios.post(
        `${API}/admin/upload-apk/init`,
        null,
        {
          params: { filename: file.name },
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      const { upload_id } = initRes.data;

      // Step 2: send file in chunks
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunkBlob = file.slice(start, end);
        const chunkForm = new FormData();
        chunkForm.append('chunk', chunkBlob, file.name);

        await axios.post(`${API}/admin/upload-apk/chunk`, chunkForm, {
          params: { upload_id, chunk_index: i },
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      // Step 3: complete upload -> assembled server-side + pushed to Object Storage
      const completeRes = await axios.post(
        `${API}/admin/upload-apk/complete`,
        null,
        {
          params: { upload_id, filename: file.name },
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );

      toast.success("APK subido exitosamente");
      setConfig({
        ...config,
        apk_url: completeRes.data.url,
        apk_filename: completeRes.data.filename
      });
      setManualUrl(completeRes.data.url);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Error al subir el APK");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSaveUrl = async () => {
    if (!manualUrl.trim()) {
      toast.error("Ingresa una URL válida");
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/app-config`,
        { 
          apk_url: manualUrl.trim(),
          apk_version: version.trim() || null
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast.success("Configuración guardada");
      fetchConfig();
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error("Usa una imagen PNG, JPG o WebP");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("El logo no puede superar 5MB");
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/admin/app-config/logo`, formData, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      localStorage.setItem('brand_logo_url', response.data.logo_url);
      toast.success("Logo actualizado - home, footer y splash ya lo usan");
      fetchConfig();
      fetchLogoLibrary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir el logo");
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleResetLogo = async () => {
    setUploadingLogo(true);
    try {
      await axios.delete(`${API}/admin/app-config/logo`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      localStorage.removeItem('brand_logo_url');
      toast.success("Logo restablecido al predeterminado");
      fetchConfig();
      fetchLogoLibrary();
    } catch (error) {
      toast.error("Error al restablecer el logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSelectLibraryLogo = async (logoId) => {
    setSelectingLogoId(logoId);
    try {
      const response = await axios.post(`${API}/admin/app-config/logo-library/${logoId}/select`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      localStorage.setItem('brand_logo_url', response.data.logo_url);
      toast.success("Logo activado");
      fetchConfig();
      fetchLogoLibrary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al activar el logo");
    } finally {
      setSelectingLogoId(null);
    }
  };

  const handleDeleteLibraryLogo = async (logoId) => {
    try {
      await axios.delete(`${API}/admin/app-config/logo-library/${logoId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Logo eliminado de la galería");
      fetchLogoLibrary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo eliminar el logo");
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <p className="text-[#64748B]">Acceso denegado</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Header />
      <main className="max-w-2xl mx-auto pt-32 pb-24 px-4">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2">
            <Smartphone className="text-[#F97316]" />
            APP Android
          </h1>
          <p className="text-sm text-[#64748B]">Gestiona el archivo APK para descarga</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="animate-spin text-[#F97316]" size={32} />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Current Status Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  {config?.apk_url ? (
                    <CheckCircle2 className="text-green-500" size={20} />
                  ) : (
                    <AlertCircle className="text-amber-500" size={20} />
                  )}
                  Estado Actual
                </CardTitle>
                <CardDescription>
                  {config?.apk_url 
                    ? "Hay un APK disponible para descarga" 
                    : "No hay APK configurado"}
                </CardDescription>
              </CardHeader>
              {config?.apk_url && (
                <CardContent>
                  <div className="bg-[#F1F5F9] rounded-lg p-4 space-y-2">
                    <p className="text-sm">
                      <span className="font-medium">Archivo:</span>{" "}
                      <span className="text-[#64748B]">{config.apk_filename || "APK"}</span>
                    </p>
                    {config.apk_version && (
                      <p className="text-sm">
                        <span className="font-medium">Versión:</span>{" "}
                        <span className="text-[#64748B]">{config.apk_version}</span>
                      </p>
                    )}
                    <a 
                      href={config.apk_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <Download size={14} />
                      Descargar APK actual
                    </a>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Upload New APK Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Upload size={20} />
                  Subir Nuevo APK
                </CardTitle>
                <CardDescription>
                  Sube un archivo .apk desde tu computadora (máx 200MB, se envía en partes)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".apk"
                  className="hidden"
                  data-testid="apk-file-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full bg-[#F97316] hover:bg-[#EA580C]"
                  data-testid="apk-select-file-button"
                >
                  {uploading ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Subiendo... {uploadProgress}%
                    </>
                  ) : (
                    <>
                      <Upload size={18} className="mr-2" />
                      Seleccionar archivo APK
                    </>
                  )}
                </Button>
                {uploading && (
                  <div className="mt-3 w-full bg-[#F1F5F9] rounded-full h-2 overflow-hidden" data-testid="apk-upload-progress-bar">
                    <div
                      className="bg-[#F97316] h-2 rounded-full transition-all duration-200"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Manual URL Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Link2 size={20} />
                  O usa una URL externa
                </CardTitle>
                <CardDescription>
                  Si tienes el APK alojado en otro servicio, pega la URL aquí
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>URL del APK</Label>
                  <Input
                    value={manualUrl}
                    onChange={(e) => setManualUrl(e.target.value)}
                    placeholder="https://ejemplo.com/ibiid-app.apk"
                  />
                </div>
                <div>
                  <Label>Versión (opcional)</Label>
                  <Input
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    placeholder="1.0.0"
                  />
                </div>
                <Button
                  onClick={handleSaveUrl}
                  disabled={saving || !manualUrl.trim()}
                  variant="outline"
                  className="w-full"
                >
                  {saving ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    "Guardar URL"
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Brand Logo Card */}
            <Card data-testid="brand-logo-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ImageIcon size={20} />
                  Logo de la marca
                </CardTitle>
                <CardDescription>
                  Cambia el logo que se ve en el home (header), el pie de página y la pantalla de carga
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-white border rounded-lg p-4 flex items-center justify-center">
                  <img
                    src={config?.logo_url || "/logo-marketvalley.png"}
                    alt="Logo actual"
                    className="h-12 w-auto"
                    data-testid="current-logo-preview"
                  />
                </div>
                <input
                  type="file"
                  ref={logoInputRef}
                  onChange={handleLogoUpload}
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  data-testid="logo-file-input"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="flex-1 bg-[#F97316] hover:bg-[#EA580C]"
                    data-testid="logo-select-file-button"
                  >
                    {uploadingLogo ? (
                      <>
                        <Loader2 size={18} className="mr-2 animate-spin" />
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Upload size={18} className="mr-2" />
                        Subir nuevo logo
                      </>
                    )}
                  </Button>
                  {config?.logo_url && (
                    <Button
                      onClick={handleResetLogo}
                      disabled={uploadingLogo}
                      variant="outline"
                      data-testid="logo-reset-button"
                    >
                      <RotateCcw size={16} className="mr-2" />
                      Restablecer
                    </Button>
                  )}
                </div>
                <p className="text-xs text-[#94A3B8]">
                  PNG, JPG o WebP (máx 5MB). PNG con fondo transparente da mejor resultado.
                </p>
                <div className="bg-[#F0F9FF] border border-[#BAE6FD] rounded-lg p-3">
                  <p className="text-xs font-semibold text-[#0369A1] mb-1">✨ Redimensionado automático</p>
                  <p className="text-xs text-[#0C4A6E]">
                    Cualquier imagen que subas se centra y ajusta automáticamente en un lienzo
                    cuadrado de 512×512 px con margen y fondo transparente — no necesitas
                    recortarla ni redimensionarla tú mismo antes de subirla.
                  </p>
                </div>

                {logoLibrary.length > 0 && (
                  <div className="pt-2">
                    <p className="text-sm font-medium text-[#0F172A] mb-2">Galería de logos guardados</p>
                    <div className="grid grid-cols-4 gap-2" data-testid="logo-library-grid">
                      {logoLibrary.map((item) => (
                        <div key={item.logo_id} className="relative group">
                          <button
                            onClick={() => !item.is_active && handleSelectLibraryLogo(item.logo_id)}
                            disabled={selectingLogoId === item.logo_id}
                            className={`w-full aspect-square rounded-lg border-2 flex items-center justify-center bg-white p-2 transition-colors ${
                              item.is_active ? "border-[#F97316]" : "border-[#E2E8F0] hover:border-[#CBD5E1]"
                            }`}
                            data-testid={`logo-library-item-${item.logo_id}`}
                            title={item.is_active ? "Logo activo" : "Usar este logo"}
                          >
                            {selectingLogoId === item.logo_id ? (
                              <Loader2 size={16} className="animate-spin text-[#F97316]" />
                            ) : (
                              <img src={item.url} alt="Logo guardado" className="max-h-full max-w-full object-contain" />
                            )}
                          </button>
                          {item.is_active && (
                            <span className="absolute -top-1.5 -right-1.5 bg-[#F97316] text-white rounded-full p-0.5" data-testid={`logo-library-active-badge-${item.logo_id}`}>
                              <Check size={10} />
                            </span>
                          )}
                          {!item.is_active && (
                            <button
                              onClick={() => handleDeleteLibraryLogo(item.logo_id)}
                              className="absolute -top-1.5 -right-1.5 bg-white border border-[#E2E8F0] text-[#94A3B8] hover:text-red-600 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`logo-library-delete-${item.logo_id}`}
                              title="Eliminar de la galería"
                            >
                              <Trash2 size={10} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-[#94A3B8] mt-1.5">Toca un logo guardado para volver a usarlo.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Admin WhatsApp Alerts Card */}
            <Card data-testid="admin-whatsapp-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Smartphone size={20} />
                  Alertas de actividad por WhatsApp
                </CardTitle>
                <CardDescription>
                  Número que recibe un WhatsApp cuando un cliente puja, ofrece o se interesa en un producto
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label htmlFor="admin-whatsapp-input">Número (con código de país, ej. +50760000000)</Label>
                  <Input
                    id="admin-whatsapp-input"
                    value={adminWhatsapp}
                    onChange={(e) => setAdminWhatsapp(e.target.value)}
                    placeholder="+50760000000"
                    data-testid="admin-whatsapp-input"
                  />
                </div>
                <Button
                  onClick={handleSaveWhatsappNumber}
                  disabled={savingWhatsapp || !adminWhatsapp}
                  className="bg-[#475569] hover:bg-[#334155]"
                  data-testid="admin-whatsapp-save-button"
                >
                  {savingWhatsapp ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                  Guardar número
                </Button>
                <p className="text-xs text-[#94A3B8]">
                  Estos mensajes se enviarán por Twilio en cuanto la cuenta esté activa.
                </p>
              </CardContent>
            </Card>

            {/* Preview of how it looks */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Vista previa</CardTitle>
                <CardDescription>
                  Así se verá el botón de descarga en el header
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-white border rounded-lg p-4 flex items-center justify-center">
                  <div className="flex items-center gap-1 px-2 py-1 text-[#0F172A] hover:text-[#F97316] transition-colors group cursor-pointer">
                    <Smartphone size={16} className="group-hover:animate-bounce" />
                    <span className="text-[10px] font-bold">
                      Descarga nuestra APP android
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
};

export default AdminAppConfigPage;
